<p-card>
  <ng-template pTemplate="title">
    <div class="flex flex-wrap items-center gap-2">
      <span>Autenticación en dos pasos</span>
      <p-skeleton *ngIf="loadingStatus" width="6rem" height="1.5rem"></p-skeleton>
      <p-tag
        *ngIf="!loadingStatus"
        [value]="totpEnabled ? 'Activada' : 'Desactivada'"
        [severity]="totpEnabled ? 'success' : 'secondary'"
        [icon]="totpEnabled ? 'pi pi-check-circle' : 'pi pi-info-circle'"
      ></p-tag>
    </div>
  </ng-template>

  <ng-template pTemplate="subtitle">
    Solicita un código temporal de tu app autenticadora (Google Authenticator, Microsoft Authenticator, 1Password, Authy) cada vez que inicies sesión.
  </ng-template>

  <p-message *ngIf="totpEnabled" severity="success" icon="pi pi-shield" text="Tu cuenta pide un código de verificación además de la contraseña."></p-message>

  <p-message
    *ngIf="!totpEnabled && !loadingStatus"
    severity="warn"
    icon="pi pi-exclamation-triangle"
    text="Sin segundo factor, tu cuenta depende únicamente de la contraseña."
  ></p-message>

  <ng-template pTemplate="footer">
    <div class="flex justify-end">
      <p-button
        *ngIf="!totpEnabled"
        label="Configurar"
        icon="pi pi-qrcode"
        iconPos="left"
        severity="primary"
        [disabled]="loadingStatus"
        (onClick)="openSetupDialog.emit()"
      ></p-button>

      <p-button
        *ngIf="totpEnabled"
        label="Desactivar"
        icon="pi pi-times-circle"
        iconPos="left"
        severity="danger"
        variant="outlined"
        [disabled]="loadingStatus"
        (onClick)="openDisableDialog.emit()"
      ></p-button>
    </div>
  </ng-template>
</p-card>

<p-dialog
  header="Configurar autenticación en dos pasos"
  [visible]="showSetupDialog"
  (visibleChange)="!$event && closeSetupDialog.emit()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [closable]="!startingEnrollment && !confirmingEnrollment"
  styleClass="w-[min(92vw,40rem)]"
>
  <p-stepper [value]="setupStepIndex" (valueChange)="onStepperValueChange($event)" [linear]="true" styleClass="w-full pt-2">
    <p-step-list>
      <p-step [value]="1">Contraseña</p-step>
      <p-step [value]="2">Código QR</p-step>
      <p-step [value]="3">Confirmación</p-step>
    </p-step-list>

    <p-step-panels>
      <p-step-panel [value]="1">
        <ng-template #content>
          <form [formGroup]="totpPasswordForm" class="flex flex-col gap-4 pt-2" (ngSubmit)="submitEnrollmentPassword.emit()">
            <p-message severity="info" icon="pi pi-lock" text="Confirma tu contraseña actual para vincular una nueva app autenticadora."></p-message>

            <div class="flex flex-col gap-2">
              <label for="totpCurrentPassword" class="font-bold">Contraseña actual</label>
              <p-password
                id="totpCurrentPassword"
                formControlName="currentPassword"
                [feedback]="false"
                [toggleMask]="true"
                inputStyleClass="w-full"
                styleClass="w-full"
              ></p-password>
            </div>
          </form>
        </ng-template>
      </p-step-panel>

      <p-step-panel [value]="2">
        <ng-template #content>
          <div class="flex flex-col gap-4 pt-2">
            <p-message severity="info" icon="pi pi-mobile" text="Escanea este código con tu app autenticadora para vincular tu cuenta."></p-message>

            <div class="flex justify-center">
              <p-image *ngIf="qrDataUrl" [src]="qrDataUrl" alt="Código QR de configuración TOTP" width="180" [preview]="true"></p-image>
              <p-skeleton *ngIf="!qrDataUrl" width="180px" height="180px"></p-skeleton>
            </div>

            <p-fieldset legend="¿No puedes escanear el código?" [toggleable]="true" [collapsed]="true">
              <div class="flex flex-col gap-2">
                <label for="totpSecret" class="font-bold">Clave de configuración</label>
                <p-inputgroup>
                  <input id="totpSecret" type="text" pInputText [value]="secret" readonly class="w-full" />
                  <p-inputgroup-addon>
                    <p-button
                      icon="pi pi-copy"
                      variant="text"
                      severity="secondary"
                      pTooltip="Copiar clave"
                      tooltipPosition="top"
                      (onClick)="onCopySecret()"
                    ></p-button>
                  </p-inputgroup-addon>
                </p-inputgroup>
                <small>Ingrésala manualmente en tu app autenticadora como clave basada en tiempo.</small>
              </div>
            </p-fieldset>
          </div>
        </ng-template>
      </p-step-panel>

      <p-step-panel [value]="3">
        <ng-template #content>
          <form [formGroup]="totpVerifyForm" class="flex flex-col gap-4 pt-2" (ngSubmit)="submitEnrollmentCode.emit()">
            <p-message severity="info" icon="pi pi-key" text="Escribe el código de 6 dígitos que muestra tu app autenticadora."></p-message>

            <div class="flex flex-col items-center gap-2">
              <label for="totpCode" class="font-bold">Código de verificación</label>
              <p-inputotp id="totpCode" formControlName="code" [length]="6" [integerOnly]="true"></p-inputotp>
              <small class="text-center">El código cambia cada 30 segundos. Si falla, revisa que la hora de tu dispositivo esté sincronizada.</small>
            </div>

            <div class="flex flex-col gap-2">
              <label for="totpDeviceName" class="font-bold">Nombre del dispositivo</label>
              <input id="totpDeviceName" type="text" pInputText formControlName="deviceName" class="w-full" placeholder="Ej: iPhone de Álvaro" />
              <small>Te ayudará a reconocer la app autenticadora vinculada.</small>
            </div>
          </form>
        </ng-template>
      </p-step-panel>
    </p-step-panels>
  </p-stepper>

  <ng-template pTemplate="footer">
    <div class="flex w-full flex-wrap items-center justify-between gap-2">
      <p-button
        label="Cancelar"
        icon="pi pi-times"
        iconPos="left"
        severity="danger"
        variant="outlined"
        [disabled]="startingEnrollment || confirmingEnrollment"
        (onClick)="closeSetupDialog.emit()"
      ></p-button>

      <div class="flex flex-wrap items-center gap-2">
        <p-button
          *ngIf="setupStep === 'verify'"
          label="Volver al QR"
          icon="pi pi-arrow-left"
          iconPos="left"
          severity="secondary"
          variant="outlined"
          [disabled]="confirmingEnrollment"
          (onClick)="goToStep('qr')"
        ></p-button>

        <p-button
          *ngIf="setupStep === 'password'"
          label="Continuar"
          icon="pi pi-arrow-right"
          iconPos="right"
          severity="success"
          [loading]="startingEnrollment"
          [disabled]="totpPasswordForm.invalid"
          (onClick)="submitEnrollmentPassword.emit()"
        ></p-button>

        <p-button
          *ngIf="setupStep === 'qr'"
          label="Continuar"
          icon="pi pi-arrow-right"
          iconPos="right"
          severity="success"
          (onClick)="goToStep('verify')"
        ></p-button>

        <p-button
          *ngIf="setupStep === 'verify'"
          label="Activar"
          icon="pi pi-check"
          iconPos="left"
          severity="success"
          [loading]="confirmingEnrollment"
          [disabled]="totpVerifyForm.invalid"
          (onClick)="submitEnrollmentCode.emit()"
        ></p-button>
      </div>
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  header="Desactivar autenticación en dos pasos"
  [visible]="showDisableDialog"
  (visibleChange)="!$event && closeDisableDialog.emit()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [closable]="!disablingTotp"
  styleClass="w-[min(92vw,34rem)]"
>
  <form [formGroup]="totpDisableForm" class="flex flex-col gap-4 pt-2" (ngSubmit)="submitDisableTotp.emit()">
    <p-message severity="warn" icon="pi pi-exclamation-triangle" text="Al desactivarla, tu cuenta volverá a protegerse solo con la contraseña."></p-message>

    <div class="flex flex-col gap-2">
      <label for="disableTotpPassword" class="font-bold">Contraseña actual</label>
      <p-password
        id="disableTotpPassword"
        formControlName="currentPassword"
        [feedback]="false"
        [toggleMask]="true"
        inputStyleClass="w-full"
        styleClass="w-full"
      ></p-password>
    </div>
  </form>

  <ng-template pTemplate="footer">
    <div class="flex w-full flex-wrap items-center justify-between gap-2">
      <p-button
        label="Cancelar"
        icon="pi pi-times"
        iconPos="left"
        severity="secondary"
        variant="outlined"
        [disabled]="disablingTotp"
        (onClick)="closeDisableDialog.emit()"
      ></p-button>

      <p-button
        label="Desactivar"
        icon="pi pi-times-circle"
        iconPos="left"
        severity="danger"
        [loading]="disablingTotp"
        [disabled]="totpDisableForm.invalid"
        (onClick)="submitDisableTotp.emit()"
      ></p-button>
    </div>
  </ng-template>
</p-dialog>
